import { useEffect, useState } from "react";
import { Button } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";
import { authorizeNativePasswordReset } from "@/api/native-email-recovery";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";
import { commonMessages } from "@/i18n/messages/common";

type Props = {
  uid: string;
  token: string;
  context: string;
  onBrowser: () => void;
  onReady: (url: string) => void;
};

export function NativePasswordResetReturn({
  uid,
  token,
  context,
  onBrowser,
  onReady,
}: Props) {
  const t = useT(authMessages);
  const tc = useT(commonMessages);
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError(false);
    setUrl("");
    void authorizeNativePasswordReset({ uid, token, context })
      .then((next) => {
        if (active) {
          setUrl(next);
          onReady(next);
        }
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [uid, token, context, attempt, onReady]);
  return (
    <div className="auth-state-stack" data-testid="native-reset-return">
      <p className="auth-page-copy">{t("finishResetInDenker")}</p>
      {url ? (
        <a
          href={url}
          className={buttonVariants({ variant: "secondary", size: "lg" })}
          rel="noreferrer"
          data-testid="native-reset-open"
        >
          {t("openDenker")}
        </a>
      ) : error ? (
        <>
          <p role="alert" className="auth-page-copy">
            {t("nativeReturnFailed")}
          </p>
          <Button
            variant="secondary"
            data-testid="native-reset-retry"
            onClick={() => setAttempt((value) => value + 1)}
          >
            {tc("retry")}
          </Button>
        </>
      ) : (
        <p role="status" className="auth-page-copy">
          {t("preparingReturn")}
        </p>
      )}
      <Button
        variant="ghost"
        disabled={!url && !error}
        data-testid="native-reset-browser"
        onClick={onBrowser}
      >
        {t("resetOnWebsite")}
      </Button>
    </div>
  );
}
